import React, { useEffect, useState } from 'react';
import { styled } from '@mui/material';

import { useSnackbar } from 'contexts/snackbar';
import { useViewport } from 'contexts/viewport';
import groupService from 'services/group';
import useFormValidation from 'services/hooks/useFormValidation';
import { Button, Form, IconButton, ResponsiveDialog, Stack, Text, TextArea, TextInput } from 'shell/components';

const EditChannelDialog = ({
    activeGroupId,
    channels,
    setChannels,
    activeChatId,
    editChannelDialog,
    setEditChannelDialog,
}) => {
    const { message } = useSnackbar();
    const { device } = useViewport();

    const [form, setForm] = useState({
        name: '',
        description: '',
    });
    const [hasClickedSubmit, setHasClickedSubmit] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const getErrorMessage = (key, value) => {
        if (['name', 'description'].includes(key)) value = value?.trim();
        switch (key) {
            case 'name':
                if (!value) return 'Please enter a channel name';
                if (value === 'general') return 'Please enter another name';
                return '';
            default:
                return '';
        }
    };

    const { errors, validate, validateAll } = useFormValidation(form, getErrorMessage);

    const handleClose = () => setEditChannelDialog({ open: false });

    const handleChange = (e) => {
        const { name: key, value } = e.target;
        setForm((current) => ({ ...current, [key]: value }));
        if (hasClickedSubmit || errors[key]) {
            validate(key, value);
        }
    };

    const handleSubmit = async () => {
        if (!hasClickedSubmit) setHasClickedSubmit(true);
        if (!validateAll()) return;

        setIsLoading(true);
        await groupService.updateChannel(activeGroupId, {
            id: activeChatId,
            name: form.name,
            description: form.description,
        });
        setChannels((current) =>
            current.map((channel) => {
                if (channel.id === activeChatId) return { ...channel, ...form };
                return channel;
            })
        );
        setIsLoading(false);
        message.success('Channel details have been updated.');
        handleClose();
    };

    useEffect(() => {
        if (channels && activeChatId) {
            const channel = channels?.find(({ id }) => id === activeChatId);
            if (channel?.chat_type === 'channel') {
                setForm({
                    name: channel.name,
                    description: channel.description,
                });
            }
        }
    }, [channels, activeChatId]);

    return (
        <ResponsiveDialog open={editChannelDialog.open} onClose={handleClose}>
            <CustomStack device={device} spacing={4}>
                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Text variant={'h6'}>Edit Channel Details</Text>
                    <IconButton
                        aria-label={'close'}
                        kind={'exit'}
                        size={'medium'}
                        variant={'square'}
                        onClick={handleClose}
                    />
                </Stack>
                <Form onSubmit={handleSubmit}>
                    <Stack alignItems={'flex-end'} spacing={4}>
                        <TextInput
                            error={Boolean(errors.name)}
                            helperText={errors.name}
                            label={'Channel Name'}
                            name={'name'}
                            placeholder={'Channel Name'}
                            required
                            value={form.name}
                            onChange={handleChange}
                        />
                        <TextArea
                            label={'Channel Description'}
                            name={'description'}
                            placeholder={'(Optional)'}
                            rows={4}
                            value={form.description}
                            onChange={handleChange}
                        />
                        <Button loading={isLoading} type={'submit'}>
                            Update
                        </Button>
                    </Stack>
                </Form>
            </CustomStack>
        </ResponsiveDialog>
    );
};

const CustomStack = styled(Stack)(({ device }) => {
    return {
        ...(device !== 'mobile' && {
            width: '20rem',
        }),
    };
});

export default EditChannelDialog;
